import { Column, Entity, Index, OneToMany } from 'typeorm';
import type { BrandAttributionValue } from '../../config/instance-settings-registry';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';

/**
 * PD-16557 — a brand is the look and voice a company presents: logos,
 * colors, contacts and email sender. A tenant has exactly one DEFAULT brand
 * (seeded from the Company Profile) and any number of named brands; each
 * company points at one through `company.fk_brand__id`, and a company with
 * no brand uses the default.
 *
 * Brand is separate from company so several companies can share one look
 * (HF's four Great Wall companies). There is deliberately no hierarchy: a
 * variant such as a city in the logo is a separate brand, made with
 * Duplicate. A blank field on a named brand falls back to the default
 * brand's value — see resolveBranding in utils/branding.ts, which is the
 * only place that merge happens.
 *
 * What stays elsewhere: storefront behavior and app store links on
 * portal_site; the CRM logo and page title on the Company Profile (they
 * describe the staff app, not anything sent out); legal name, remit-to and
 * PDF templates per company.
 */
@Index('IDX_brand_isDefault', ['isDefault'])
@Entity('brand')
export class BrandEntity extends BaseEntity {
	// The name customers see: email sender name fallback, "company name" in
	// email and PDF headers, the storefront tab title. Unique among
	// non-deleted brands (enforced by the service; a DB unique index would
	// block re-using a soft-deleted brand's name).
	@Column('varchar', { name: 'name', length: 128 })
	public name!: string;

	// Exactly one non-deleted row per tenant is the default (service
	// enforced; the seed migration creates it).
	@Column({ type: 'boolean', name: 'isDefault', width: 1, default: () => "'0'" })
	public isDefault!: boolean;

	// Wordmark for dark surfaces (the storefront header and pre-login
	// backdrop); consumers fall back to it when logoUrlLight is unset.
	@Column('varchar', { name: 'logoUrl', nullable: true, length: 512 })
	public logoUrl!: string | null;

	// Wordmark variant for light surfaces.
	@Column('varchar', { name: 'logoUrlLight', nullable: true, length: 512 })
	public logoUrlLight!: string | null;

	// Logo in the header of every email sent for this brand's companies.
	@Column('varchar', { name: 'emailLogoUrl', nullable: true, length: 512 })
	public emailLogoUrl!: string | null;

	@Column('varchar', { name: 'faviconUrl', nullable: true, length: 512 })
	public faviconUrl!: string | null;

	// Hero image behind the storefront sign-in screen.
	@Column('varchar', {
		name: 'loginBackgroundUrl',
		nullable: true,
		length: 512,
	})
	public loginBackgroundUrl!: string | null;

	// The storefront app's icon, shown on the app promotion page (PD-16558).
	@Column('varchar', { name: 'appIconUrl', nullable: true, length: 512 })
	public appIconUrl!: string | null;

	// Palette (PD-16045): primary, and an accent per mode. Validated with
	// brandColorProblem on save.
	@Column('varchar', { name: 'primaryColor', nullable: true, length: 16 })
	public primaryColor!: string | null;

	@Column('varchar', { name: 'accentColor', nullable: true, length: 16 })
	public accentColor!: string | null;

	@Column('varchar', {
		name: 'accentColorLight',
		nullable: true,
		length: 16,
	})
	public accentColorLight!: string | null;

	// Theme of the storefront's pre-login surfaces; null = 'dark'.
	@Column('varchar', { name: 'loginTheme', nullable: true, length: 8 })
	public loginTheme!: 'light' | 'dark' | null;

	// "Powered by" attribution override; null inherits the tenant setting.
	@Column('json', { name: 'brandAttribution', nullable: true })
	public brandAttribution!: BrandAttributionValue | null;

	@Column('varchar', { name: 'supportPhone', nullable: true, length: 64 })
	public supportPhone!: string | null;

	@Column('varchar', { name: 'supportEmail', nullable: true, length: 255 })
	public supportEmail!: string | null;

	@Column('varchar', {
		name: 'supportBusinessHours',
		nullable: true,
		length: 255,
	})
	public supportBusinessHours!: string | null;

	/** One line for the footer of customer emails: who sent this, and from where. */
	@Column('varchar', { name: 'mailingAddress', nullable: true, length: 255 })
	public mailingAddress!: string | null;

	@Column('varchar', { name: 'salesPhone', nullable: true, length: 64 })
	public salesPhone!: string | null;

	@Column('varchar', { name: 'salesEmail', nullable: true, length: 255 })
	public salesEmail!: string | null;

	// PD-17321 — where customers call or write about a bill. The payment-link,
	// payment-method-link and sent-form pages show it in place of the support
	// contact, and fall back to the support contact while both are blank.
	@Column('varchar', { name: 'billingPhone', nullable: true, length: 64 })
	public billingPhone!: string | null;

	@Column('varchar', { name: 'billingEmail', nullable: true, length: 255 })
	public billingEmail!: string | null;

	// Outbound sender. The domain must be one of the tenant's verified
	// sending domains; domain and local part only apply as a pair.
	@Column('varchar', { name: 'emailFromDomain', nullable: true, length: 150 })
	public emailFromDomain!: string | null;

	@Column('varchar', {
		name: 'emailFromLocalPart',
		nullable: true,
		length: 64,
	})
	public emailFromLocalPart!: string | null;

	@Column('varchar', { name: 'emailFromName', nullable: true, length: 150 })
	public emailFromName!: string | null;

	/**
	 * The tenant number, in international form, that automated texts to this
	 * brand's companies' customers come from (sign-in, welcome and delivery
	 * texts). Named brands only: null, and always on the default brand, means
	 * the tenant's choice per kind of text in Settings → Communications → Text
	 * Messages. Must be one of the tenant's text-capable numbers when chosen.
	 */
	@Column('varchar', { name: 'textFromNumber', nullable: true, length: 20 })
	public textFromNumber!: string | null;

	/**
	 * PD-17377 — the brand's storefront welcome email, an `email_template` row
	 * this brand owns (metadata.ownerType 'brand'). Null until generated, and
	 * a null or missing row sends the built-in welcome. Its three texts
	 * (welcome, password reset, sign-in) are in brand_text_template.
	 */
	@Column('int', {
		name: 'fk_email_template__id_welcome',
		nullable: true,
		unsigned: true,
	})
	public fkEmailTemplateIdWelcome!: number | null;

	/** PD-17377 — the brand's storefront forgot-password email; as above. */
	@Column('int', {
		name: 'fk_email_template__id_passwordReset',
		nullable: true,
		unsigned: true,
	})
	public fkEmailTemplateIdPasswordReset!: number | null;

	/** PD-17377 — the brand's storefront sign-in code email; as above. */
	@Column('int', {
		name: 'fk_email_template__id_signIn',
		nullable: true,
		unsigned: true,
	})
	public fkEmailTemplateIdSignIn!: number | null;

	@OneToMany(() => CompanyEntity, (company) => company.fkBrand)
	public companies!: Array<CompanyEntity>;
}
